import {
	ClientTypeEnum,
	EditClientFormData,
	SocietyClientEditFormData,
	UserClientEditFormData,
} from "@dune/types";
import {
	Button,
	ControlledInput,
	ControlledPhoneInput,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	RootDialogProps,
} from "@dune/ui";
import { ClientDetailsWithConstructionSites } from "@dune/vars";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { useUpdateClient } from "../../../../../api/mutations/clients.mutations";
import { useGetSocietyInfoFromSirenNumber } from "../../../../../api/queries/siren.queries";

export const EditClientDialog = (
	props: RootDialogProps & { client: ClientDetailsWithConstructionSites },
) => {
	const { client } = props;
	const isUser = client.clientType === ClientTypeEnum.USER;

	const methods = useForm<EditClientFormData>({
		defaultValues: getInitialValues(client),
	});

	const {
		control,
		handleSubmit,
		setValue,
		watch,
		reset,
		formState: { isSubmitting },
	} = methods;
	const { mutate: updateClient } = useUpdateClient();

	const formData = watch();
	const sirenValue = !isUser && "siren" in formData ? formData.siren : "";
	const { data: sirenData, isSuccess: isSirenSuccess } =
		useGetSocietyInfoFromSirenNumber(sirenValue || "", false);

	useEffect(() => {
		if (!isUser && isSirenSuccess && sirenData) {
			setValue("denomination", sirenData.denomination || "");
		}
	}, [isSirenSuccess, sirenData, setValue, isUser]);

	useEffect(() => {
		if (client) {
			reset(getInitialValues(client));
		}
	}, [client, reset]);

	const onSubmit = (data: EditClientFormData) => {
		if (isUser && "firstName" in data) {
			const clientData = {
				type: ClientTypeEnum.USER,
				firstName: data.firstName,
				lastName: data.lastName,
				...(data.email?.trim() && { email: data.email }),
				...(data.mobilePhone && { mobilePhone: data.mobilePhone }),
				...((data.streetNumber ||
					data.streetName ||
					data.postalCode ||
					data.city) && {
					address: {
						streetNumber: data.streetNumber || "",
						streetName: data.streetName || "",
						postalCode: data.postalCode || "",
						city: data.city || "",
					},
				}),
			};

			updateClient(
				{
					clientId: String(client.id),
					clientData,
				},
				{
					onSuccess: () => {
						toast.success("Informations client mises à jour");
						props.onOpenChange?.(false);
					},
					onError: (error: Error) => {
						toast.error("Erreur lors de la mise à jour");
						console.error(error);
					},
				},
			);
		} else if (!isUser && "siren" in data) {
			const clientData = {
				type: ClientTypeEnum.SOCIETY,
				siren: data.siren,
				denomination: data.denomination || "",
				isSyndic: false,
				...((data.contactFirstName ||
					data.contactLastName ||
					data.contactEmail?.trim() ||
					data.contactMobilePhone ||
					data.contactStreetNumber ||
					data.contactStreetName ||
					data.contactPostalCode ||
					data.contactCity) && {
					contactInfo: {
						firstName: data.contactFirstName || "",
						lastName: data.contactLastName || "",
						...(data.contactEmail?.trim() && { email: data.contactEmail }),
						...(data.contactMobilePhone && {
							mobilePhone: data.contactMobilePhone,
						}),
						...((data.contactStreetNumber ||
							data.contactStreetName ||
							data.contactPostalCode ||
							data.contactCity) && {
							address: {
								streetNumber: data.contactStreetNumber || "",
								streetName: data.contactStreetName || "",
								postalCode: data.contactPostalCode || "",
								city: data.contactCity || "",
							},
						}),
					},
				}),
			};

			updateClient(
				{
					clientId: String(client.id),
					clientData,
				},
				{
					onSuccess: () => {
						toast.success("Informations client mises à jour");
						props.onOpenChange?.(false);
					},
					onError: (error: Error) => {
						toast.error("Erreur lors de la mise à jour");
						console.error(error);
					},
				},
			);
		}
	};

	return (
		<Dialog {...props}>
			<DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>
						Modifier les informations {isUser ? "client" : "société"}
					</DialogTitle>
				</DialogHeader>

				<FormProvider {...methods}>
					<form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
						{isUser ? (
							<div className="space-y-6">
								<div>
									<h3 className="text-lg font-medium mb-4">
										Informations personnelles
									</h3>
									<div className="grid grid-cols-2 gap-4">
										<ControlledInput
											control={control}
											name="firstName"
											label="Prénom"
											required
										/>
										<ControlledInput
											control={control}
											name="lastName"
											label="Nom"
											required
										/>
									</div>
									<div className="grid grid-cols-2 gap-4 mt-4">
										<ControlledInput
											control={control}
											name="email"
											label="Email"
											type="email"
										/>
										<ControlledPhoneInput
											control={control}
											name="mobilePhone"
											label="Téléphone mobile"
										/>
									</div>
								</div>

								<div>
									<h3 className="text-lg font-medium mb-4">Adresse</h3>
									<div className="grid grid-cols-3 gap-4">
										<ControlledInput
											control={control}
											name="streetNumber"
											label="Numéro"
										/>
										<div className="col-span-2">
											<ControlledInput
												control={control}
												name="streetName"
												label="Nom de rue"
											/>
										</div>
									</div>
									<div className="grid grid-cols-2 gap-4 mt-4">
										<ControlledInput
											control={control}
											name="postalCode"
											label="Code postal"
											maxLength={5}
										/>
										<ControlledInput
											control={control}
											name="city"
											label="Ville"
										/>
									</div>
								</div>
							</div>
						) : (
							<div className="space-y-6">
								<div>
									<h3 className="text-lg font-medium mb-4">
										Informations société
									</h3>
									<div className="grid grid-cols-2 gap-4">
										<ControlledInput
											control={control}
											name="siren"
											label="SIREN"
											placeholder="9 chiffres"
											maxLength={9}
											required
										/>
										<ControlledInput
											control={control}
											name="denomination"
											label="Dénomination"
											disabled
											className="bg-gray-50"
											placeholder="Auto-rempli via SIREN"
										/>
									</div>
									{isSirenSuccess && sirenData && (
										<div className="text-sm text-green-600 mt-2">
											✓ Entreprise trouvée: {sirenData.denomination}
										</div>
									)}
								</div>

								<div>
									<h3 className="text-lg font-medium mb-4">
										Informations contact
									</h3>
									<div className="grid grid-cols-2 gap-4">
										<ControlledInput
											control={control}
											name="contactFirstName"
											label="Prénom"
											required
										/>
										<ControlledInput
											control={control}
											name="contactLastName"
											label="Nom"
											required
										/>
									</div>
									<div className="grid grid-cols-2 gap-4 mt-4">
										<ControlledInput
											control={control}
											name="contactEmail"
											label="Email"
											type="email"
										/>
										<ControlledPhoneInput
											control={control}
											name="contactMobilePhone"
											label="Téléphone mobile"
										/>
									</div>
								</div>

								<div>
									<h3 className="text-lg font-medium mb-4">Adresse contact</h3>
									<div className="grid grid-cols-3 gap-4">
										<ControlledInput
											control={control}
											name="contactStreetNumber"
											label="Numéro"
										/>
										<div className="col-span-2">
											<ControlledInput
												control={control}
												name="contactStreetName"
												label="Nom de rue"
											/>
										</div>
									</div>
									<div className="grid grid-cols-2 gap-4 mt-4">
										<ControlledInput
											control={control}
											name="contactPostalCode"
											label="Code postal"
											maxLength={5}
										/>
										<ControlledInput
											control={control}
											name="contactCity"
											label="Ville"
										/>
									</div>
								</div>
							</div>
						)}

						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => props.onOpenChange?.(false)}
								disabled={isSubmitting}
							>
								Annuler
							</Button>
							<Button
								type="submit"
								isLoading={isSubmitting}
								disabled={isSubmitting}
							>
								Enregistrer
							</Button>
						</DialogFooter>
					</form>
				</FormProvider>
			</DialogContent>
		</Dialog>
	);
};

function getInitialValues(
	client: ClientDetailsWithConstructionSites,
): EditClientFormData {
	if (client.clientType === ClientTypeEnum.USER) {
		const contact =
			client.constructionSiteClientContacts[0]?.constructionSiteContact;
		const userFormData: UserClientEditFormData = {
			firstName: contact?.firstname || "",
			lastName: contact?.lastname || "",
			email: contact?.email || "",
			mobilePhone: contact?.mobilePhone || "",
			streetNumber: contact?.address?.streetNumber || "",
			streetName: contact?.address?.streetName || "",
			postalCode: contact?.address?.postalCode || "",
			city: contact?.address?.city || "",
		};
		return userFormData;
	}

	const society = client.clientSociety;
	const contactInfo = society?.syndicUserAccessToPlatform;
	const societyFormData: SocietyClientEditFormData = {
		siren: society?.siren || "",
		denomination: society?.denomination || "",
		contactFirstName: contactInfo?.firstname || "",
		contactLastName: contactInfo?.lastname || "",
		contactEmail: contactInfo?.email || "",
		contactMobilePhone: contactInfo?.mobilePhone || "",
		contactStreetNumber: contactInfo?.address?.streetNumber || "",
		contactStreetName: contactInfo?.address?.streetName || "",
		contactPostalCode: contactInfo?.address?.postalCode || "",
		contactCity: contactInfo?.address?.city || "",
	};
	return societyFormData;
}
